import styled from '@emotion/styled'

import { initials } from '@bemlo/utils'

import { theme } from '../constants'
import { Color } from '../constants/color'
import { StyleProps, createStyles } from '../utils'
export type AvatarType = 'image' | 'initials' | 'profile'

type AvatarElementProps = {
  image?: string | null
  squared?: boolean
  size: number
  type: AvatarType
  background?: string
  styles?: StyleProps
}

const AvatarElement = styled.div<AvatarElementProps>((props) => ({
  alignItems: props.type === 'profile' ? 'flex-end' : 'center',
  backgroundColor: {
    image: Color.TRANSPARENT,
    initials: props.background ?? Color.LIGHTER_BLUE,
    profile: props.background ?? Color.BLUE,
  }[props.type],
  backgroundImage: props.image ? `url(${props.image})` : undefined,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  borderRadius: props.squared
    ? props.size <= 24
      ? theme.border.radius.small
      : theme.border.radius.semi
    : 999,
  color: props.image ? 'transparent' : Color.BLUE,
  display: 'flex',
  flexShrink: 0,
  fontSize: props.size / 2.8,
  fontWeight: theme.font.weight.bold,
  height: props.size,
  justifyContent: 'center',
  pointerEvents: 'none',
  position: 'relative',
  userSelect: 'none',
  width: props.size,
  ...createStyles(props.styles),
}))

const User = styled.svg({
  bottom: 0,
  left: '50%',
  position: 'absolute',
  transform: 'translateX(-50%)',
})

const UserClip = styled.div<{ squared?: boolean }>(({ squared }) => ({
  // This ensures that there's no ugly aliased edge along the border:
  bottom: -0.5,
  borderRadius: squared ? theme.border.radius.semi : 999,
  left: 0,
  overflow: 'hidden',
  position: 'absolute',
  right: 0,
  top: 0,
}))

export type AvatarProps = {
  className?: string
  image?: string | undefined | null
  name?: string | undefined | null
  squared?: boolean
  /**
   * NOTE: Specifying the size as a number will be deprecated in the future.
   */
  size?: AvatarSize | number
  type?: AvatarType
  defaultBackground?: string
  styles?: StyleProps
}

export type AvatarSize = keyof typeof theme.elements.avatar.size

export const Avatar = (props: AvatarProps) => {
  const size =
    typeof props.size === 'number'
      ? props.size
      : theme.elements.avatar.size[props.size ?? 'md']
  const type = props.type ?? 'image'

  const iconSize = size * 0.9
  const userInitials = props.name ? initials(props.name) : ''

  return (
    <AvatarElement
      className={props.className}
      image={type === 'initials' ? undefined : props.image}
      size={size}
      squared={props.squared}
      styles={props.styles}
      background={props.defaultBackground}
      type={type === 'image' && !props.image ? 'initials' : type}
    >
      {type === 'profile' ? (
        <UserClip>
          <User
            fill="none"
            height={`${iconSize}px`}
            viewBox="0 0 28 28"
            width={`${iconSize}px`}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.1575 -0.000167693C9.51747 -0.000167693 7.48747 3.72411 7.48747 7.50411C7.48747 12.4598 10.562 14.6998 10.562 16.3527C10.562 16.9969 10.0976 17.8084 8.82205 18.4527L2.03547 21.9241C-0.342301 23.1841 0.846003 27.9998 0.846003 27.9998H27.4387C27.4387 27.9998 28.5987 23.1841 26.2209 21.9241L19.4355 18.4241C18.1883 17.7799 17.6083 17.0799 17.6083 16.4357C17.6083 14.9241 20.7406 12.7671 20.7406 7.4471C20.7689 3.02394 18.3334 -0.000335693 14.1579 -0.000335693L14.1575 -0.000167693Z"
              fill={Color.LIGHTEST_BLUE}
            />
          </User>
        </UserClip>
      ) : (
        userInitials
      )}
    </AvatarElement>
  )
}
